import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { ButtonView, ImageScaleView, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { downscale } from 'src/conf/scale';
import sounds from 'src/lib/sounds';
import StateObserver from 'src/StateObserver';
import { SuperLollipopProgress } from '../components/SuperLollipopProgress';
import { SuperLollipopSadPets } from '../components/SuperLollipopSadPets';
import { getSuperLollipopWinStreakRequired } from 'src/replicant/superLollipop/superLollipop.getters';
import { user } from 'src/StateObserver';

type CloseHandler = () => void;

export default class PopupSuperLollipopLost extends PopupBasicLazy {
  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.POPUP_LOST,
      },
      zIndex: 10000,
    });

    this.buttonClose.hide();

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    // This popup should close on any tap
    this.box.updateOpts({
      clip: true,
    });

    // Screen tap area
    new ButtonView({
      superview: this.box,
      width: width,
      height: height,
      zIndex: 1,
      onClick: () => {
        sounds.sfx.play('ok');
        opts.close();
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/superLollipop/shared']),
      ...AssetGroup.constructURLs(['assets/ui/superLollipop/popupLost']),
    ]);
  }

  public async init() {
    super.init({});
  }

  public async fadeIn() {
    await super.fadeIn();
    this.animateIn();
  }

  private async animateIn() {
    await waitForItPromise(300);

    const locale = StateObserver.getLocale();
    const en = locale === 'en';

    sounds.sfx.play('puzzle-lose-new');

    const title = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/superLollipop/popupLost/title_lost_${locale}.png`,
      width: downscale(en ? 803 : 691),
      height: downscale(en ? 98 : 92),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 300,
      centerOnOrigin: true,
    });
    await this.showAnimated(title);

    const startOver = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/superLollipop/popupLost/start_over_${locale}.png`,
      width: downscale(en ? 292 : 487),
      height: downscale(en ? 54 : 62),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: title.style.y + 60,
      centerOnOrigin: true,
    });
    await this.showAnimated(startOver);

    const sadPets = new SuperLollipopSadPets({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: this.box.style.height * 0.5 - 70,
      centerOnOrigin: true,
    });
    await this.showAnimated(sadPets);

    const progressBar = new SuperLollipopProgress({
      superview: this.box,
      centerOnOrigin: true,
      scale: 0.8,
      x: this.box.style.width * 0.5,
      y: this.box.style.height * 0.5 + 120,
    });
    progressBar.setProgress(getSuperLollipopWinStreakRequired(user()));
    await this.showAnimated(progressBar);
    progressBar.animateProgress(0);

    const cta = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/superLollipop/shared/tap_to_continue_${locale}.png`,
      width: downscale(en ? 569 : 433),
      height: downscale(en ? 87 : 84),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 960,
      centerOnOrigin: true,
    });
    await this.showAnimated(cta);
  }

  private async showAnimated(view: View) {
    const scale = view.style.scale;
    view.updateOpts({ scale: 0 });
    animate(view).then({ scale }, 300, animate.easeOutBack);
    sounds.sfx.play('puzzle-ice-tap');
    await waitForItPromise(200);
  }
}
